import { LitElement, html, css } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import type { Role } from '../types/chat.js';

@customElement('befit-chat-bubble')
export class ChatBubble extends LitElement {
  static override styles = css`
    :host {
      display: flex;
      margin: 6px 0;
    }
    :host([role='user']) {
      justify-content: flex-end;
    }
    .bubble {
      padding: 10px 14px;
      border-radius: 14px;
      line-height: 1.5;
      font-size: 13px;
      word-wrap: break-word;
    }
    :host([role='user']) .bubble {
      max-width: 80%;
      background: var(--befit-primary, #101828);
      color: white;
      border-bottom-right-radius: 4px;
    }
    :host([role='assistant']) {
      width: 100%;
    }
    :host([role='assistant']) .bubble {
      width: 100%;
      box-sizing: border-box;
      background: white;
      color: var(--befit-heading, #1D2939);
      border: 1px solid var(--befit-border, #E5E7EB);
      border-bottom-left-radius: 4px;
    }
    .bubble :is(a) {
      color: var(--befit-primary, #101828);
      text-decoration: underline;
      text-decoration-color: var(--befit-accent, #8FFE09);
      text-decoration-thickness: 2px;
      text-underline-offset: 3px;
    }
    :host([role='user']) .bubble :is(a) {
      color: var(--befit-accent, #8FFE09);
      text-decoration-color: currentColor;
    }
    .bubble :is(p) { margin: 0 0 8px 0; }
    .bubble :is(p:last-child) { margin-bottom: 0; }
    .bubble :is(ul, ol) { margin: 4px 0 4px 18px; padding: 0; }
    .bubble :is(code) {
      background: var(--befit-bg-soft, #F9FAFB);
      padding: 1px 5px;
      border-radius: 4px;
      font-size: 12.5px;
    }
    .bubble :is(table) {
      width: 100%;
      border-collapse: collapse;
      margin: 8px 0;
      font-size: 11.5px;
      line-height: 1.35;
      display: block;
      overflow-x: auto;
    }
    .bubble :is(th, td) {
      padding: 5px 7px;
      border: 1px solid var(--befit-border, #E5E7EB);
      text-align: left;
      vertical-align: top;
    }
    .bubble :is(thead th) {
      background: var(--befit-primary, #101828);
      color: white;
      font-weight: 600;
    }
    .bubble :is(tbody tr:nth-child(odd) td) {
      background: var(--befit-bg-soft, #F9FAFB);
    }
    .bubble :is(tbody tr:nth-child(even) td) {
      background: white;
    }
    @media (max-width: 480px) {
      .bubble :is(table) {
        display: block;
        overflow: visible;
      }
      .bubble :is(thead) {
        display: none;
      }
      .bubble :is(tbody, tbody tr) {
        display: block;
      }
      .bubble :is(tbody tr) {
        margin: 0 0 8px 0;
        border: 1px solid var(--befit-border, #E5E7EB);
        border-radius: 8px;
        overflow: hidden;
      }
      .bubble :is(tbody td) {
        display: flex !important;
        justify-content: space-between;
        align-items: baseline;
        gap: 12px;
        padding: 8px 11px;
        border: none;
        border-bottom: 1px solid var(--befit-border, #E5E7EB);
        background: white;
        font-size: 12.5px;
        line-height: 1.4;
        word-break: break-word;
      }
      .bubble :is(tbody tr:nth-child(odd) td),
      .bubble :is(tbody tr:nth-child(even) td) {
        background: white;
      }
      .bubble :is(tbody td:first-child) {
        background: #101828 !important;
        color: white !important;
        font-weight: 700;
        font-size: 13.5px;
        padding: 11px 12px;
        justify-content: flex-start;
      }
      /* The bot wraps plan names in **bold** - the resulting <strong>/<b>
         inherits color by default, but in some browsers (Brave shields,
         certain iOS WKWebView contexts) we've seen the strong override the
         td's color back to its own default. Force it to inherit so the
         plan name stays visible on the dark header. */
      .bubble :is(tbody td:first-child) :is(strong, b, em, i) {
        color: inherit !important;
        font-weight: 700;
      }
      .bubble :is(tbody td:last-child) {
        border-bottom: none;
      }
      .bubble :is(tbody td:not(:first-child))::before {
        content: attr(data-label);
        font-weight: 600;
        color: var(--befit-text, #475467);
        flex-shrink: 0;
        min-width: 70px;
      }
      /* Defensive: if a cell ends up without a data-label (table without
         a thead, or DOMPurify stripped the attr), don't render an empty
         label box that pushes the value to the right. */
      .bubble :is(tbody td:not(:first-child))[data-label='']::before,
      .bubble :is(tbody td:not(:first-child)):not([data-label])::before {
        content: '';
        min-width: 0;
      }
    }
    .bf-cursor {
      display: inline-block;
      color: var(--befit-accent, #8FFE09);
      margin-left: 2px;
      font-weight: 400;
      line-height: 1;
      animation: bf-blink 1s steps(2, jump-none) infinite;
    }
    @keyframes bf-blink {
      0%, 50%       { opacity: 1; }
      50.01%, 100%  { opacity: 0; }
    }
  `;

  @property({ type: String, reflect: true }) override role: Role = 'assistant';
  @property({ type: String }) content = '';
  @property({ type: Boolean }) pending = false;

  override render() {
    const raw = marked.parse(this.content, { async: false }) as string;
    const labeled = injectTableLabels(raw);
    const parsed = DOMPurify.sanitize(labeled, {
      ADD_ATTR: ['data-label', 'target', 'rel'],
    });
    const externalized = openLinksInNewTab(parsed);
    const html_ = this.pending ? injectCursor(externalized) : externalized;
    return html`<div class="bubble">${unsafeHTML(html_)}</div>`;
  }
}

// All anchors render with target="_blank" + rel="noopener noreferrer" so
// clicking a Maps / directions link doesn't replace the page and trash
// the chat session.
function openLinksInNewTab(rawHtml: string): string {
  if (!rawHtml.includes('<a ')) return rawHtml;
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(`<div id="r">${rawHtml}</div>`, 'text/html');
    const root = doc.getElementById('r');
    if (!root) return rawHtml;
    root.querySelectorAll('a[href]').forEach((a) => {
      a.setAttribute('target', '_blank');
      a.setAttribute('rel', 'noopener noreferrer');
    });
    return root.innerHTML;
  } catch {
    return rawHtml;
  }
}

// Adds data-label="ColumnHeader" to each <td> based on the matching <th>
// in the table head. Lets the responsive (≤480px) card layout show
// "Label: Value" lines without needing the bot to author the table
// differently.
function injectTableLabels(rawHtml: string): string {
  if (!rawHtml.includes('<table')) return rawHtml;
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(`<div id="r">${rawHtml}</div>`, 'text/html');
    const root = doc.getElementById('r');
    if (!root) return rawHtml;
    root.querySelectorAll('table').forEach((table) => {
      const headers = Array.from(table.querySelectorAll('thead th')).map(
        (th) => (th.textContent || '').trim(),
      );
      if (headers.length === 0) return;
      table.querySelectorAll('tbody tr').forEach((tr) => {
        Array.from(tr.children).forEach((cell, i) => {
          const label = headers[i];
          if (label) (cell as HTMLElement).setAttribute('data-label', label);
        });
      });
    });
    return root.innerHTML;
  } catch {
    return rawHtml;
  }
}

const CURSOR_HTML = '<span class="bf-cursor">▌</span>';

function injectCursor(rawHtml: string): string {
  if (!rawHtml.trim()) return CURSOR_HTML;
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(`<div id="r">${rawHtml}</div>`, 'text/html');
    const root = doc.getElementById('r');
    if (!root) return rawHtml + CURSOR_HTML;
    let target: Element = root;
    while (target.lastElementChild) target = target.lastElementChild;
    const cursor = doc.createElement('span');
    cursor.className = 'bf-cursor';
    cursor.textContent = '▌';
    target.appendChild(cursor);
    return root.innerHTML;
  } catch {
    return rawHtml + CURSOR_HTML;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'befit-chat-bubble': ChatBubble;
  }
}
